"use client";

import type { ChatStatus } from "ai";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import type { AgnoChatMessage } from "@/hooks/use-agno-chat";
import { useFollowUpSuggestions } from "@/hooks/use-follow-up-suggestions";
import type { AccessTokenGetter } from "@/lib/utils";
import type { FollowUpSuggestionsEnvelope } from "@/api/follow-up-suggestions";

/** Only canonical, complete assistant rows can identify a successful last turn. */
export function lastCompletedAssistantMessageId(messages: AgnoChatMessage[]): string | null {
  const last = messages.at(-1);
  if (!last || last.role !== "assistant" || last.metadata?.status !== "complete" ||
      typeof last.id !== "string" || !last.id.trim() ||
      !(last.content?.trim() || last.parts.some((part) => part.type === "text" && part.text.trim()))) return null;
  return last.id;
}

export interface FollowUpSuggestionsProps {
  sessionId: string;
  lastAssistantMessageId: string | null;
  completedSuccessfully: boolean;
  writable: boolean;
  status: ChatStatus;
  getToken: AccessTokenGetter;
  bootstrapCapability?: number;
  bootstrapSuggestions?: FollowUpSuggestionsEnvelope | null;
  bootstrapPending?: boolean;
  hasDraft: boolean;
  onSubmit: (message: { text: string; files: [] }) => void | Promise<void>;
}

export function FollowUpSuggestions({ sessionId, lastAssistantMessageId, completedSuccessfully, writable, status, getToken, bootstrapCapability, bootstrapSuggestions, bootstrapPending, hasDraft, onSubmit }: FollowUpSuggestionsProps) {
  const { t } = useTranslation("chat");
  const { suggestions, clear } = useFollowUpSuggestions({ sessionId, lastAssistantMessageId, completedSuccessfully, writable, status, getToken, bootstrapCapability, bootstrapSuggestions, bootstrapPending });
  if (hasDraft || !writable || status !== "ready" || !completedSuccessfully || suggestions.length === 0) return null;
  return <div role="group" aria-label={t("followUpSuggestions.groupLabel")} className="flex w-full min-w-0 flex-wrap items-center gap-1">
    {suggestions.slice(0, 3).map((suggestion) => <Button
      key={suggestion.id}
      type="button"
      size="sm"
      variant="outline"
      className="w-full min-w-0 justify-start gap-1 px-2 md:w-auto md:max-w-lg"
      aria-label={t("followUpSuggestions.submitName", { prompt: suggestion.prompt })}
      title={suggestion.prompt}
      onClick={() => { clear(); void onSubmit({ text: suggestion.prompt, files: [] }); }}
    ><span className="truncate">{suggestion.label}</span></Button>)}
  </div>;
}
